import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { NumberInput } from '@/components/form/NumberInput.tsx';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import TwoInputsRow from '@/components/form/ui/TwoInputsRow.tsx';
import {
  MONTH_OPTIONS,
  getPreviousMonth,
} from '@/features/dashboard/tfg/types/month.ts';
import {
  deklaracjaPreviewSchema,
  DeklaracjaPreviewFormData,
  DeklaracjaPreviewFormInput,
} from '@/features/dashboard/tfg/schemas/deklaracjaPreviewSchema.ts';
import { getDeklaracjaPreviewQueryOptions } from '@/features/dashboard/tfg/hooks/useGetDeklaracjaPreview.ts';
import { TfgDeklaracjaPreviewDto } from '@/features/dashboard/tfg/types/TfgDeklaracjaPreviewDto.ts';

interface GenerateDeklaracjaDialogProps {
  onClose: () => void;
  onSuccess: (data: TfgDeklaracjaPreviewDto) => void;
}

const GenerateDeklaracjaDialog = ({
  onClose,
  onSuccess,
}: GenerateDeklaracjaDialogProps) => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const previousMonth = getPreviousMonth();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DeklaracjaPreviewFormInput, unknown, DeklaracjaPreviewFormData>({
    resolver: zodResolver(deklaracjaPreviewSchema),
    defaultValues: {
      year: previousMonth.year,
      month: String(previousMonth.month),
    },
  });

  const onSubmit = async (data: DeklaracjaPreviewFormData) => {
    const params = { year: data.year, month: Number(data.month) };
    setIsLoading(true);
    try {
      const result = await queryClient.fetchQuery(
        getDeklaracjaPreviewQueryOptions(params),
      );
      onSuccess(result);
    } catch (error) {
      toast.error('Wystąpił błąd', {
        description:
          (error as Error).message ||
          'Nie udało się wygenerować deklaracji TFG.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader title="Wygeneruj deklarację TFG" onClose={onClose} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalContent>
          <TwoInputsRow>
            <NumberInput
              label="Rok"
              register={register('year')}
              placeholder="2026"
              required
              min={2020}
              max={2100}
              error={errors.year?.message}
            />
            <SingleSelectInput<DeklaracjaPreviewFormInput>
              fieldName="month"
              control={control}
              options={MONTH_OPTIONS}
              label="Miesiąc"
              required
              placeholder="Wybierz miesiąc..."
              error={errors.month?.message}
            />
          </TwoInputsRow>
        </ModalContent>
        <ModalFooter>
          <ActionButton tone="ghost" size="sm" type="button" onClick={onClose}>
            Anuluj
          </ActionButton>
          <ActionButton
            tone="accent"
            size="sm"
            type="submit"
            isLoading={isLoading}
          >
            Wygeneruj deklarację
          </ActionButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default GenerateDeklaracjaDialog;
